import * as Sentry from "@sentry/react";
import { GraphQLResult, generateClient } from "aws-amplify/api";
import { AuthUser, fetchAuthSession, fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { User, UserForResolver } from "db/definitions";
import { useEffect, useRef, useState } from "react";
import { recordSentry } from "utils/recordSentry";
import { createUserDocForResolver, getFirstUser } from "../db/queries/userQueries";
import { createUser, updateUser } from "../graphql/mutations";
// @ts-ignore
import { getUser } from "../graphql/queries";
import updatedUser from "../utils/_updatedUser";
import { useSingleton } from "./useSingleton";
import { useAnalytics } from "./useAnalytics";

import { detectDeviceType } from "../utils/deviceDetection";
import { UseAuthenticator } from "@aws-amplify/ui-react";

function useUser(online: boolean, signOut?: UseAuthenticator["signOut"]) {
	const [userDoc, setUserDoc] = useState<UserForResolver | undefined | null>(null);
	const [fullUser, setFullUser] = useState<User | undefined | null>(null);
	const stopListenerCallback = useRef<(() => void) | null>(null);
	const { handleEndSession } = useSingleton();

	// Initialize analytics for user lifecycle tracking
	const { trackUserBehaviorEvent, trackSystemEvent } = useAnalytics({
		userId: userDoc?.id || "",
		groupId: userDoc?.group || "",
		online,
		autoStart: false, // Don't auto-start since RootContainer handles session_start
	});

	// Session management is handled by RootContainer globally using analytics.endSession()

	/**
	 * initial setup for the inactivity monitoring and the auth listener for the user
	 */
	useEffect(() => {
		/**
		 * This function is meant to make sure the actual userDoc is created or retrieved (for analytics)
		 */
		async function handleUserDoc(userData: AuthUser): Promise<{ userDocument: UserForResolver | undefined; fullUserDoc: User | undefined }> {
			const attr = await fetchUserAttributes();
			// Users migrated to the replacement user pool (2026-10-06) keep their original User.id (old Cognito sub)
			// in custom:legacy_id. Users created after the migration don't have it and use their own sub.
			const userDocId = attr["custom:legacy_id"] || attr.sub;
			const email = attr.email;

			// Make sure userDocId exists
			if (!userDocId) {
				throw new Error("No user ID found in attributes");
			}

			// attempt to retrieve the user doc
			let userDocument: UserForResolver | undefined;
			let fullUserDocument: User | undefined;
			try {
				const client = generateClient({ authMode: "userPool" });
				let retrieved = (await client.graphql({
					query: getUser,
					variables: {
						id: userDocId,
					},
					authMode: "userPool",
				})) as GraphQLResult<any>;

				if (!retrieved.data?.getUser) {
					// no user doc yet
					// going to need to figure out a better way to assign groups...

					const userSession = await fetchAuthSession();
					const groups = userSession.tokens?.accessToken.payload["cognito:groups"] as string[] | undefined;
					if (groups) {
						const group = groups.find((grp: string) => !grp.includes("_"));
						if (group) {
							// ADD roleIds: ["UUID_FOR_DEFAULT_ROLE"] to this guy
							// Then, fetch the role + perms here, assemble into full user. That way, we have all of that if the user
							// goes offline
							// - Eric
							const newUserDoc = {
								username: userData.username,
								id: userDocId,
								hideTutorial: false,
								lightMode: true,
								group,
								roleIds: [import.meta.env.VITE_APP_DEFAULT_ROLE || ""],
								email: email || "",
							};

							const userResult = (await client.graphql({
								query: createUser,
								variables: {
									input: newUserDoc,
								},
								authMode: "userPool",
							})) as GraphQLResult<{ createUser: UserForResolver }>;

							userDocument = userResult.data.createUser;
						}
					}
				} else {
					// the document exists
					const getUserResult = retrieved.data.getUser;
					if (getUserResult) {
						userDocument = getUserResult as any; // Type assertion to handle GraphQL/local type differences
						fullUserDocument = getUserResult as any; // Type assertion to handle GraphQL/local type differences

						if (userDocument && userDocument.hideTutorial === null) {
							const userObject = {
								id: userDocument.id,
								hideTutorial: false,
							};
							try {
								const result = (await client.graphql({
									query: updateUser,
									variables: {
										input: userObject,
									},
									authMode: "userPool",
								})) as GraphQLResult<{ updateUser: UserForResolver }>;
								return { userDocument: result.data.updateUser, fullUserDoc: fullUserDocument };
							} catch (error) {
								recordSentry({ error, msg: "error updating single user:" });
							}
						}
					}
				}
			} catch (error) {
				recordSentry({ error, msg: "Error getting/creating userDoc:" });
			}
			return { userDocument: userDocument, fullUserDoc: fullUserDocument };
		}
		async function authListener(): Promise<void> {
			if (online) {
				const hubListener = Hub.listen("auth", async (data) => {
					switch (data.payload.event) {
						// THIS IS NEVER BEING CALLED, BECAUSE THIS IS DONE IN ROOT CONTAINER, WHICH IS AFTER THE USER LOGS IN. THAT
						// COMPONENT IS NOT RENDERED UNTIL THEN
						case "signedIn":
							const { userDocument, fullUserDoc } = await handleUserDoc(data.payload.data);
							setUserDoc(userDocument);
							if (fullUserDoc) setFullUser(fullUserDoc);
							if (userDocument) Sentry.setUser({ name: userDocument.username, email: userDocument.email, online: online });
						case "signedOut":
							setUserDoc(null);
							hubListener();
						case "tokenRefresh":
							// TokenRefresh is the typical one, it just refreshes the user's token to keep them signed in
							break;
						case "tokenRefresh_failure":
							// Refresh token has expired — the user must sign in again. Not an error, just sign them out cleanly.
							if (signOut) handleEndSession(signOut);
							break;
						default:
							return null;
					}
				});

				try {
					const newUser = await getCurrentUser();
					const { userDocument, fullUserDoc } = await handleUserDoc(newUser);
					setUserDoc(userDocument);
					if (fullUserDoc) setFullUser(fullUserDoc);
					if (userDocument) Sentry.setUser({ name: userDocument.username, email: userDocument.email, online: online });
				} catch (error) {
					recordSentry({ error });
				}
				stopListenerCallback.current = hubListener;
			} else {
				const user = await getFirstUser();
				if (user) {
					setUserDoc(createUserDocForResolver(user));
					setFullUser(user);
					Sentry.setUser({ name: user.username, email: user.email, online: online });
				} else {
					setUserDoc(undefined);
					setFullUser(undefined);
				}
			}
		}
		authListener();
		return () => {
			stopListenerCallback.current?.();
			stopListenerCallback.current = null;
		};
	}, []);

	async function handleUpdateUser(data: UserForResolver) {
		const { id, username, email, group, hideTutorial, lightMode, isDeveloper } = data;
		const userObject = {
			id,
			username,
			email,
			group,
			hideTutorial,
			lightMode,
			isDeveloper,
		};
		const update = await updatedUser(userObject);
		if (update) {
			setUserDoc({ ...userObject, ...update.data.updateUser });
			Sentry.setUser({ name: update.data.updateUser.username, email: update.data.updateUser.email, online: online });
		}
	}

	function handleSignOut() {
		// Session end is handled by RootContainer via analytics.endSession()
		// before this function is called
		handleEndSession(signOut);
	}

	const results = {
		userDoc,
		fullUser,
		handleSignOut,
		handleUpdateUser,
	};

	return results;
}

export default useUser;
